// The one parser for a number a person TYPED, read in the convention they typed
// it in (#1088 Phase 2, moved here for #1121).
//
// It lived in `apps/web/src/app/locale.ts` while its only callers were web
// forms. `parseLength` in floorPlanUnits.ts needs the same reading and cannot
// import out of an app, so the parser moved down rather than being written a
// second time: two implementations of "what does 1.234 mean" is exactly how the
// floor plan and the list sheet would come to disagree about a user's keystroke.
// `app/locale.ts` re-exports this, so every existing web call site is unchanged.
//
// Nothing here formats. The display side stays in `app/locale.ts` because it is
// Intl-bound and locale-resolved at render; this half is pure string reading and
// has to be callable from shared code with the locale handed to it.
const GROUP_SEPARATOR = { en: ",", es: "." };
const DECIMAL_SEPARATOR = { en: ".", es: "," };
/** The character `locale` puts between the whole part and the fraction. */
export function decimalSeparator(locale) {
    return DECIMAL_SEPARATOR[locale];
}
/**
 * Parse a number a user typed, in their locale.
 *
 * Which fields need this, and which must NOT have it.
 *
 * A real `<input type="number">` has a `.value` that is dot-decimal by spec
 * whatever the locale, and the browser rejects a comma before it ever reaches
 * us. Those fields — the shopping price and budget drafts, the finance amount
 * and balance forms, the floor plan's own plan-size box — are already
 * locale-proof and must stay that way: localising what is written INTO them
 * makes the browser blank the field.
 *
 * A plain `<input>` carrying only `inputMode="decimal"` is NOT one of those.
 * `inputMode` hints at a phone keyboard and guarantees nothing, so the app owns
 * both halves of the round trip. The list sheet, the food catalog and every
 * floor-plan length field are of this kind, and an earlier version of this
 * comment asserted the opposite — which is why this function shipped wired to
 * nothing while a Spanish "12,34" was saved as 1234.00. Check the element
 * before trusting either rule.
 *
 * It REJECTS rather than guesses. "1.234" is one-point-two-three-four in en and
 * one thousand two hundred thirty four in es; there is no recovering the user's
 * intent from the string alone. A rejection is a visible form error; a guess
 * writes a plausible-but-wrong integer into an amount column with no signal.
 */
export function parseLocaleNumber(input, locale) {
    const group = GROUP_SEPARATOR[locale];
    const decimal = DECIMAL_SEPARATOR[locale];
    // Strip currency decoration and whitespace, including the non-breaking space
    // Intl emits between amount and symbol.
    //
    // ORDER MATTERS. Removing "$" first turns "US$" into a bare "US" that the
    // currency-code pass can no longer match, and "US" then fails the
    // digits-and-separators test below — so the one decoration Spanish
    // actually produces for USD was the one input this rejected.
    const cleaned = input
        .replace(/US\$|USD/gi, "")
        .replace(/[$€£ \s]/g, "")
        .trim();
    if (!cleaned)
        return null;
    const sign = cleaned.startsWith("-") ? -1 : 1;
    const body = cleaned.replace(/^[+-]/, "");
    if (!body)
        return null;
    // A separator on its own is punctuation, not a number. Checked here because
    // the missing-integer-part rule below would otherwise read "." as zero.
    if (!/[0-9]/.test(body))
        return null;
    // Anything that is not a digit or one of this locale's two separators means
    // the value was typed in some other convention.
    const allowed = new RegExp(`^[0-9${escapeForClass(group)}${escapeForClass(decimal)}]+$`);
    if (!allowed.test(body))
        return null;
    const decimalParts = body.split(decimal);
    if (decimalParts.length > 2)
        return null;
    const [whole = "", fraction = ""] = decimalParts;
    if (fraction.includes(group))
        return null;
    // Group separators must actually group: 1,234,567 yes; 1,23,4 no. A bare
    // "1234.5" under es is rejected for the same reason — under es that dot is a
    // thousands separator and a 1-digit tail is not a group.
    if (whole.includes(group)) {
        const groups = whole.split(group);
        const [lead, ...rest] = groups;
        if (!lead || lead.length < 1 || lead.length > 3)
            return null;
        if (rest.some((part) => part.length !== 3))
            return null;
    }
    // A missing integer part is how people write a half: ".5" in English, ",5" in
    // Spanish. The old parser rejected both, and these cells have no validation
    // error to show for it — a List Sheet quantity of ".5" simply synced as null,
    // and the Recipe Builder dropped the ingredient's amount (codex review).
    //
    // Only within its OWN locale: ".5" under es is a leading THOUSANDS separator,
    // which is not a number, and the grouping rule above still rejects it.
    const digits = whole.split(group).join("");
    const normalized = `${digits || "0"}.${fraction || "0"}`;
    if (!/^\d+\.\d+$/.test(normalized))
        return null;
    const value = Number(normalized);
    return Number.isFinite(value) ? sign * value : null;
}
function escapeForClass(value) {
    return value.replace(/[-\\\]^]/g, "\\$&");
}
